/**
 * EPIC-DM01-C: Demo Capture Hook
 * Manages swing capture attempts, upload progress, and upload completion
 *
 * Flow: callEdgeFunction('cloudflare-direct-upload') → XHR upload → callEdgeFunction('demo-run-from-uid')
 */

import { useState, useCallback, useRef } from 'react';
import { callEdgeFunction } from '@/lib/edgeFunctionClient';

export interface DemoAttempt {
  attempt: number;
  run_id: string;
  cf_stream_uid: string;
  status: 'uploading' | 'uploaded' | 'failed';
  created_at: string;
  progress?: number;
  error?: string;
}

export interface UploadProgress {
  pct: number;
  bytesSent: number;
  total: number;
}

interface DemoRunFromUidResult {
  ok: boolean;
  run_id: string;
  cf_stream_uid: string;
}

const DEMO_MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

export function useDemoCapture(sessionId: string) {
  const [attempts, setAttempts] = useState<DemoAttempt[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [currentAttempt, setCurrentAttempt] = useState<number | null>(null);
  
  const abortControllerRef = useRef<AbortController | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);

  /**
   * Get upload URL from cloudflare-direct-upload via callEdgeFunction
   * (handles auth, retries on transient/network errors, consistent error reporting)
   */
  const getDirectUrl = useCallback(async (): Promise<{ uploadURL: string; uid: string }> => {
    console.log('[useDemoCapture] Requesting direct upload URL', {
      origin: typeof window !== 'undefined' ? window.location.origin : 'unknown',
      supabaseUrl: import.meta.env.VITE_SUPABASE_URL,
    });

    const data = await callEdgeFunction<{ ok: boolean; uploadURL: string; uid: string }>(
      'cloudflare-direct-upload',
      'POST',
    );

    if (!data?.uploadURL || !data?.uid) {
      throw new Error('Invalid direct upload response: missing uploadURL or uid');
    }
    return { uploadURL: data.uploadURL, uid: data.uid };
  }, []);

  /**
   * Upload file to Cloudflare with progress tracking
   */
  const uploadFile = useCallback(async (
    file: File, 
    uploadURL: string,
    attemptIndex: number,
    onProgress?: (p: UploadProgress) => void
  ): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhrRef.current = xhr;

      // Progress tracking
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const progress = {
            pct: Math.round((e.loaded / e.total) * 100),
            bytesSent: e.loaded,
            total: e.total,
          };
          
          onProgress?.(progress);
          
          // Update attempt progress in state
          setAttempts(prev => prev.map(a => 
            a.attempt === attemptIndex 
              ? { ...a, progress: progress.pct }
              : a
          ));
        }
      };

      // Success
      xhr.onload = () => {
        xhrRef.current = null;
        if (xhr.status >= 200 && xhr.status < 300) {
          console.log('[useDemoCapture] Upload complete');
          resolve();
        } else {
          console.error('[useDemoCapture] Upload failed:', xhr.status, xhr.statusText);
          reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
        }
      };

      // Network error
      xhr.onerror = () => {
        xhrRef.current = null;
        reject(new Error('Network error during upload'));
      };

      // User cancelled
      xhr.onabort = () => {
        xhrRef.current = null;
        reject(new Error('Upload cancelled'));
      };

      // Use POST with multipart form data (matches Cloudflare's expected format)
      const formData = new FormData();
      formData.append('file', file);

      xhr.open('POST', uploadURL, true);
      xhr.send(formData);
    });
  }, []);

  /**
   * Create swing run after upload (demo-run-from-uid - working path)
   */
  const createRunFromUid = useCallback(async (
    uid: string,
    attemptIndex: number,
    originalFilename: string
  ): Promise<DemoRunFromUidResult> => {
    return callEdgeFunction<DemoRunFromUidResult>('demo-run-from-uid', 'POST', {
      demo_session_id: sessionId,
      attempt: attemptIndex,
      uid,
      original_filename: originalFilename,
    });
  }, [sessionId]);

  /**
   * Mark attempt as failed
   */
  const markFailed = useCallback((attemptIndex: number, error: string) => {
    setAttempts(prev => prev.map(a => 
      a.attempt === attemptIndex 
        ? { ...a, status: 'failed' as const, error }
        : a
    ));
  }, []);

  /**
   * Full upload flow: cloudflare-direct-upload → upload → demo-run-from-uid (same as MVP)
   */
  const uploadAttempt = useCallback(async (
    attemptIndex: number,
    file: File,
    onProgress?: (p: UploadProgress) => void
  ): Promise<string> => {
    if (file.size > DEMO_MAX_FILE_SIZE) {
      throw new Error(
        `Video must be under 25 MB for demo mode. Got ${(file.size / 1024 / 1024).toFixed(1)} MB.`,
      );
    }

    if (isUploading) {
      throw new Error('Upload already in progress');
    }

    setIsUploading(true);
    setUploadError(null);
    setCurrentAttempt(attemptIndex);
    abortControllerRef.current = new AbortController();

    try {
      // Step 1: Get upload URL (same as useDirectUpload - raw fetch)
      const { uploadURL, uid } = await getDirectUrl();

      // Add attempt to local state (uploading)
      setAttempts(prev => {
        const filtered = prev.filter(a => a.attempt !== attemptIndex);
        return [...filtered, {
          attempt: attemptIndex,
          run_id: '',
          cf_stream_uid: uid,
          status: 'uploading' as const,
          created_at: new Date().toISOString(),
          progress: 0,
        }].sort((a, b) => a.attempt - b.attempt);
      });

      // Step 2: Upload file to Cloudflare
      await uploadFile(file, uploadURL, attemptIndex, onProgress);

      // Step 3: Create swing run (demo-run-from-uid - working path)
      const { run_id } = await createRunFromUid(uid, attemptIndex, file.name);

      // Update attempt with run_id and mark uploaded
      setAttempts(prev => prev.map(a =>
        a.attempt === attemptIndex
          ? { ...a, run_id, status: 'uploaded' as const, progress: 100 }
          : a
      ));

      return run_id;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      console.error('[useDemoCapture] Upload failed:', error);
      setUploadError(errorMessage);
      markFailed(attemptIndex, errorMessage);
      throw error;
    } finally {
      setIsUploading(false);
      setCurrentAttempt(null);
      abortControllerRef.current = null;
    }
  }, [isUploading, getDirectUrl, uploadFile, createRunFromUid, markFailed]);

  /**
   * Cancel ongoing upload
   */
  const cancelUpload = useCallback(() => {
    if (xhrRef.current) {
      xhrRef.current.abort();
      xhrRef.current = null;
    }
    abortControllerRef.current?.abort();
    setIsUploading(false);
    setCurrentAttempt(null);
  }, []);

  /**
   * Retry a failed attempt
   */
  const retryAttempt = useCallback(async (
    attemptIndex: number,
    file: File,
    onProgress?: (p: UploadProgress) => void
  ): Promise<string> => {
    // Remove failed state
    setAttempts(prev => prev.filter(a => a.attempt !== attemptIndex));
    
    // Retry upload
    return uploadAttempt(attemptIndex, file, onProgress);
  }, [uploadAttempt]);

  /**
   * Initialize attempts from session meta
   */
  const initializeAttempts = useCallback((sessionAttempts: DemoAttempt[]) => {
    setAttempts(sessionAttempts);
  }, []);

  // Computed values
  const uploadedAttempts = attempts.filter(a => a.status === 'uploaded');
  const hasUploadedAttempts = uploadedAttempts.length > 0;

  return {
    // State
    attempts,
    isUploading,
    uploadError,
    currentAttempt,
    
    // Actions
    uploadAttempt,
    cancelUpload,
    retryAttempt,
    initializeAttempts,
    
    // Computed
    uploadedAttempts,
    hasUploadedAttempts,
  };
}
